import type { CardAccountReassignment, UserAccountAssignment } from '../types/additionalAccount.type';
import type { AdditionalAccountClosureRequest } from '../types/additionalAccountClosure.type';

/**
 * Traduce las asignaciones del flujo de reasignación al `reassignMode` que
 * espera `POST .../closure` (ML-41).
 *
 * Devuelve `null` cuando el payload `PER_USER` no se puede construir con ids
 * válidos. El guard viene del mock: `CompanyUserOption.id` era un string de
 * catálogo (`'usr-001'`), y `Number('usr-001')` es `NaN`, que
 * `JSON.stringify` serializa como `"userId": null`. Con el catálogo real
 * (`adapters/companyUser.adapter.ts`, `String(user.userId)`) el id siempre es
 * numérico y el guard no dispara en la práctica — se conserva igual, porque
 * que no dispare hoy no lo vuelve innecesario si el catálogo cambia de forma.
 * El consumidor debe tratar el `null` como fallo previo al request y NO llamar
 * al endpoint (ver `closeAdditionalAccount` en `additionalAccount.store.ts`).
 *
 * `NONE` y `ALL_TO_ONE` no enumeran usuarios, así que no dependen del id y
 * nunca devuelven `null`.
 *
 * `cardAssignments` (ML-126) es independiente del modo: se suma al body de los
 * tres casos y la clave se omite si no hay tarjetas, para que el body sin
 * tarjetas sea idéntico al de antes.
 */
export function buildAdditionalAccountClosureRequest(
  assignments: UserAccountAssignment[],
  assignedUsersCount: number,
  cardAssignments: CardAccountReassignment[] = []
): AdditionalAccountClosureRequest | null {
  const usersRequest = buildUsersClosureRequest(assignments, assignedUsersCount);

  if (!usersRequest || cardAssignments.length === 0) {
    return usersRequest;
  }

  return {
    ...usersRequest,
    cardAssignments: cardAssignments.map((card) => ({
      cardId: card.cardId,
      targetAccountId: card.destinationWallet.walletId,
    })),
  };
}

function buildUsersClosureRequest(
  assignments: UserAccountAssignment[],
  assignedUsersCount: number
): AdditionalAccountClosureRequest | null {
  if (assignments.length === 0) {
    return { reassignMode: 'NONE' };
  }

  const [firstAssignment] = assignments;
  const coversEveryoneToTheSameDestination =
    assignments.length === assignedUsersCount &&
    assignments.every((assignment) => assignment.destinationWallet.walletId === firstAssignment.destinationWallet.walletId);

  if (coversEveryoneToTheSameDestination) {
    return { reassignMode: 'ALL_TO_ONE', targetAccountId: firstAssignment.destinationWallet.walletId };
  }

  const userAssignments = assignments.map((assignment) => ({
    userId: Number(assignment.userId),
    targetAccountId: assignment.destinationWallet.walletId,
  }));

  const hasInvalidUserId = userAssignments.some(({ userId }) => !Number.isInteger(userId));

  return hasInvalidUserId ? null : { reassignMode: 'PER_USER', userAssignments };
}
